import React, { useReducer, useEffect } from 'react'
import { Box, Checkbox, FormControlLabel } from '@material-ui/core'
import { Icon } from 'packages/eid-icons'
import { useTranslation } from 'react-i18next'
import DateTimeFilter from '../DateTimeFilter'
import Button from '../Button'
import { isNilOrEmpty, useGetControlsAccess } from 'packages/core'
import { Tooltip } from 'packages/eid-ui'
import moment from 'moment'
import { useEditDelegation } from 'hooks'
import { styles, useStyles, ItemContainer, reducers } from './utils'

const EditDelegationForm = ({ onSave, onCancel, delegatee }) => {
    const [state, dispatch] = useReducer(reducers, {
        startDate: isNilOrEmpty(delegatee.startDateUtc)
            ? null
            : moment.utc(delegatee.startDateUtc),
        endDate: isNilOrEmpty(delegatee.endDateUtc)
            ? null
            : moment.utc(delegatee.endDateUtc),
        delegateForever: isNilOrEmpty(delegatee.endDateUtc) ? true : false,
        activeOnlyIfOutOfOffice: isNilOrEmpty(delegatee.activeOnlyIfOutOfOffice)
            ? false
            : delegatee.activeOnlyIfOutOfOffice,
    })

    const { t } = useTranslation()

    const classes = useStyles()

    const controls = useGetControlsAccess().map((c) => c.name)

    const hasOutOfOfficeAccess = controls.includes(
        'MyIdentity-PermanentDelegationsOutOfOfficePermanentDelegation-Control',
    )

    const [editDelegation, { isLoading, isSuccess }] = useEditDelegation()
    const handleEdit = () => {
        const dataToSubmit = {
            id: delegatee.id,
            delegatorPersonID: delegatee.delegatorPersonID,
            delegateePersonId: delegatee.delegateePersonId,
            delegateeFriendlyName: delegatee.delegateeFriendlyName,
            approvalFlowStepId: delegatee.approvalFlowStepId,
            approvalFlowStepFriendlyName:
                delegatee.approvalFlowStepFriendlyName,

            startDateUtc: state.startDate,
            endDateUtc: state.endDate,
        }
        if (hasOutOfOfficeAccess) {
            dataToSubmit.activeOnlyIfOutOfOffice = state.activeOnlyIfOutOfOffice
        }

        editDelegation(dataToSubmit)
    }

    useEffect(() => {
        if (isSuccess) onSave()
    }, [isSuccess])

    const endDateCheck =
        (!state.delegateForever && isNilOrEmpty(state.endDate)) ||
        state.startDate?.isAfter(state.endDate)

    return (
        <>
            <ItemContainer display="flex" marginLeft="12px">
                <FormControlLabel
                    classes={{
                        root: classes.formLabelMargin,
                        label: classes.label,
                    }}
                    control={
                        <Checkbox
                            checked={state.delegateForever}
                            icon={<Icon name="CheckBox" />}
                            checkedIcon={<Icon name="CheckedBox" />}
                            classes={{
                                checked: classes.checked,
                            }}
                            onChange={(e) =>
                                dispatch({
                                    type: 'SET_DELEGATE_FOREVER',
                                    payload: e.target.checked,
                                })
                            }
                        />
                    }
                    label={t('MyIdentity_DelegateForever')}
                />
                {hasOutOfOfficeAccess && (
                    <FormControlLabel
                        classes={{ label: classes.label }}
                        control={
                            <Checkbox
                                classes={{ checked: classes.checked }}
                                checked={state.activeOnlyIfOutOfOffice}
                                icon={<Icon name="CheckBox" />}
                                checkedIcon={<Icon name="CheckedBox" />}
                                onChange={(e) =>
                                    dispatch({
                                        type: 'SET_OUT_OF_OFFICE',
                                        payload: e.target.checked,
                                    })
                                }
                            />
                        }
                        label={t('MyIdentity_OnlyWhenOutOfOffice')}
                    />
                )}
            </ItemContainer>

            <ItemContainer display="flex" marginLeft="-8px">
                <Box padding="0px 8px" className={classes.dateFilterContainer}>
                    <DateTimeFilter
                        disablePast={true}
                        label={t('MyIdentity_StartDate')}
                        placeholder={t('MyIdentity_SelectDate')}
                        onChange={(date) => {
                            let dateToSet
                            if (!isNilOrEmpty(date)) {
                                dateToSet = moment(date)
                                    .clone()
                                    .startOf('day')
                                    .utc()
                            }
                            dispatch({
                                type: 'SET_START_DATE',
                                payload: dateToSet,
                            })
                        }}
                        value={
                            isNilOrEmpty(state.startDate)
                                ? null
                                : state.startDate.clone().local()
                        }
                    />
                </Box>

                {!state.delegateForever && (
                    <Box
                        padding="0px 8px "
                        className={classes.dateFilterContainer}
                    >
                        <DateTimeFilter
                            minDate={
                                !isNilOrEmpty(state.startDate) &&
                                state.startDate.clone().local()
                            }
                            label={t('MyIdentity_EndDate')}
                            placeholder={t('MyIdentity_SelectDate')}
                            onChange={(date) => {
                                let dateToSet
                                if (!isNilOrEmpty(date)) {
                                    dateToSet = moment(date)
                                        .clone()
                                        .endOf('day')
                                        .utc()
                                }
                                dispatch({
                                    type: 'SET_END_DATE',
                                    payload: dateToSet,
                                })
                            }}
                            value={
                                isNilOrEmpty(state.endDate)
                                    ? null
                                    : state.endDate.clone().local()
                            }
                        />
                    </Box>
                )}
            </ItemContainer>

            <ItemContainer
                display="flex"
                marginLeft="-8px"
                flexDirection="row !important"
                flexWrap="wrap"
            >
                <Tooltip
                    title={
                        endDateCheck
                            ? t('MyIdentity_RequiredFieldsMissing')
                            : ''
                    }
                >
                    <Box>
                        <Button.Rounded
                            styles={{ minWidth: '144px !important' }}
                            loading={isLoading}
                            disabled={
                                isNilOrEmpty(state.startDate) ||
                                endDateCheck ||
                                isLoading
                            }
                            onClick={() => {
                                handleEdit()
                            }}
                        >
                            {t('MyIdentity_SaveChanges')}
                        </Button.Rounded>
                    </Box>
                </Tooltip>

                <Button.Rounded onClick={onCancel} styles={styles.cancelButton}>
                    {t('MyIdentity_Cancel')}
                </Button.Rounded>
            </ItemContainer>
        </>
    )
}

export default EditDelegationForm
